import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { onAuthStateChanged, signOut as fbSignOut } from 'firebase/auth';
import { auth } from '../firebase';
import { resetApiCaches } from '../lib/api';
import { teardownWebPush } from '../lib/messaging';

const AuthContext = createContext({ user: null, loading: true, signOut: () => {} });

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastUid = useRef(undefined);

  useEffect(() => {
    // Firebase persists the session in IndexedDB, so a returning user is restored here.
    const unsub = onAuthStateChanged(auth, (u) => {
      // On an actual account change — sign-in/out, or switching to/from a managed child profile —
      // flush cached responses so we never render the previous account's data.
      if (u?.uid !== lastUid.current) {
        lastUid.current = u?.uid;
        resetApiCaches();
      }
      setUser(u);
      setLoading(false);
    });
    return unsub;
  }, []);

  const signOut = useCallback(async () => {
    // Unbind this browser's push token from the account BEFORE the Firebase session ends
    // (the unregister call needs a live ID token) — otherwise the browser keeps getting this
    // account's notifications after someone else signs in.
    await teardownWebPush();
    return fbSignOut(auth);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
